/**
 * migration-data.ts — Sentry→ɳSentry migration reference data
 *
 * SDK diff table, DSN format mapping, and env var rename table.
 * Consumed by /migrate/page.tsx.
 */

export interface SdkDiffRow {
  /** Language / framework */
  lang: string
  /** Sentry SaaS SDK package name */
  sentryPackage: string
  /** ɳSentry SDK import (Sentry-compatible — same package, new DSN) */
  nsentryPackage: string
  /** Whether a package change is needed */
  packageChange: 'none' | 'optional'
  /** Notes on compatibility */
  notes: string
}

/** Sentry SDK → ɳSentry SDK diff table.
 *  ɳSentry is Sentry-protocol-compatible. In most cases only the DSN changes.
 */
export const SDK_DIFF: SdkDiffRow[] = [
  {
    lang: 'JavaScript / TypeScript',
    sentryPackage: '@sentry/browser · @sentry/node · @sentry/nextjs',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'Sentry JS SDKs speak standard Sentry envelope protocol. Point DSN at your ɳSentry instance.',
  },
  {
    lang: 'Python',
    sentryPackage: 'sentry-sdk',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'sentry-sdk 1.x and 2.x both work. No code changes required beyond DSN.',
  },
  {
    lang: 'Go',
    sentryPackage: 'github.com/getsentry/sentry-go',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'sentry-go uses the standard Sentry envelope format. DSN swap is sufficient.',
  },
  {
    lang: 'Ruby',
    sentryPackage: 'sentry-ruby',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'sentry-ruby targets the Sentry v7 API, compatible with ɳSentry.',
  },
  {
    lang: 'PHP',
    sentryPackage: 'sentry/sentry',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'PHP SDK uses the standard envelope protocol. No code changes.',
  },
  {
    lang: 'Java / Kotlin (Android)',
    sentryPackage: 'io.sentry:sentry',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'Sentry Android SDK is compatible. Crash reporting (nself-crash, port 3841) required for symbolication.',
  },
  {
    lang: 'Swift / Objective-C (iOS)',
    sentryPackage: 'Sentry-Swift (SPM/CocoaPods)',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'iOS SDK uses the envelope protocol. Crash reporting requires nself-crash.',
  },
  {
    lang: 'Flutter / Dart',
    sentryPackage: 'sentry_flutter',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'pub.dev sentry_flutter SDK is compatible with ɳSentry DSN endpoints.',
  },
  {
    lang: 'Rust',
    sentryPackage: 'sentry (crates.io)',
    nsentryPackage: 'Same package — only DSN changes',
    packageChange: 'none',
    notes: 'sentry 0.32+ is compatible. Set dsn in ClientOptions.',
  },
]

export interface EnvVarRow {
  /** Sentry env var name */
  sentryVar: string
  /** ɳSentry equivalent */
  nsentryVar: string
  /** Example value */
  example: string
  /** Human-readable purpose */
  purpose: string
}

/** Env var rename table: Sentry SaaS → ɳSentry */
export const ENV_VAR_MAP: EnvVarRow[] = [
  {
    sentryVar: 'SENTRY_DSN',
    nsentryVar: 'SENTRY_DSN',
    example: 'https://<key>@api.local.nself.org/sentry/<project-id>',
    purpose: 'DSN — same var name, new URL pointing at your nSelf instance',
  },
  {
    sentryVar: 'SENTRY_ORG',
    nsentryVar: '(not required)',
    example: '',
    purpose: 'ɳSentry does not use Sentry cloud org slugs',
  },
  {
    sentryVar: 'SENTRY_AUTH_TOKEN',
    nsentryVar: 'NSENTRY_API_KEY',
    example: 'nself_sentry_xxxx...',
    purpose: 'Used by source-map upload CLI and CI integrations',
  },
  {
    sentryVar: 'SENTRY_PROJECT',
    nsentryVar: 'NSENTRY_PROJECT',
    example: 'my-app',
    purpose: 'Project slug on your ɳSentry instance',
  },
  {
    sentryVar: 'SENTRY_ENVIRONMENT',
    nsentryVar: 'SENTRY_ENVIRONMENT',
    example: 'production',
    purpose: 'Same var name — no change required',
  },
  {
    sentryVar: 'SENTRY_RELEASE',
    nsentryVar: 'SENTRY_RELEASE',
    example: '1.2.0',
    purpose: 'Same var name — no change required',
  },
  {
    sentryVar: 'SENTRY_TRACES_SAMPLE_RATE',
    nsentryVar: 'SENTRY_TRACES_SAMPLE_RATE',
    example: '0.1',
    purpose: 'Same var name — no change required',
  },
]

/**
 * Convert a Sentry SaaS DSN to a ɳSentry DSN.
 *
 * Sentry SaaS format:  https://<public-key>@o<org>.ingest.sentry.io/<project-id>
 * ɳSentry format:      https://<public-key>@<api-host>/sentry/<project-id>
 *
 * This is a documentation-level conversion — the actual DSN is generated by
 * `curl http://api.local.nself.org/sentry/dsn` after nself-errors is installed.
 *
 * NOTE: This function is a documented stub. The import tool endpoint
 * (POST /api/migrate/convert-dsn) returns HTTP 501 during pre-launch.
 * Full DSN conversion will be available when ɳSentry ships at v1.1.0.
 */
export function convertDsn(sentryDsn: string, apiHost: string): string {
  try {
    const url = new URL(sentryDsn)
    const publicKey = url.username
    // Extract numeric project ID from the path (last path segment)
    const projectId = url.pathname.split('/').filter(Boolean).at(-1) ?? ''
    if (!publicKey || !projectId) return ''
    return `https://${publicKey}@${apiHost}/sentry/${projectId}`
  } catch {
    return ''
  }
}
